/**
 * common CarouselDesktop component used by other mudules as a reusable component which returns ui for media carousel component. required fields can be passed as props
 *
 * @module views/components/CarouselDesktop
 * @memberof -Common
 */
import './CarouselDesktop.scss';

import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Icon from '@ulta/core/components/Icon/Icon';
import Image from '@ulta/core/components/Image/Image';
import { Link_Huge } from '@ulta/core/components/Link_Huge/Link_Huge';
import Rail, { Slide } from '@ulta/core/components/Rail/Rail';
import { useKeyPressCounter } from '@ulta/core/hooks/useKeyPressCounter/useKeyPressCounter';
import { useNextRender } from '@ulta/core/hooks/useNextRender/useNextRender';
import { hasItems } from '@ulta/core/utils/array/array';
import constants from '@ulta/core/utils/constants/constants';
import { partialDatacaptureEventAggregator } from '@ulta/core/utils/datacapture/datacapture';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import { useUuid } from '@ulta/hooks/useUuid/useUuid';

import carouselUtils from '@ulta/utils/carouselUtils/carouselUtils';

import MediaWrapper from '../MediaWrapper/MediaWrapper';
import ZoomController from '../ZoomController/ZoomController';
import * as utils from './CarouselDesktop';

/**
 * Represents a CarouselDesktop component
 *
 * @method
 * @param {CarouselDesktopProps} props - React properties passed from composition
 * @returns CarouselDesktop
 */

const CarouselDesktop = ( props ) => {
  const {
    ariaLabelForNext = defaultProps.ariaLabelForNext,
    ariaLabelForPrevious = defaultProps.ariaLabelForPrevious,
    badges,
    carouselAccessibility,
    carouselCardAccessibility,
    carouselDetailsAccessibility,
    closeAccessibilityLabel,
    componentKey,
    defaultImage = defaultProps.defaultImage,
    items,
    moveDownIconAccessibilityLabel,
    moveLeftIconAccessibilityLabel,
    moveLegendAccessibilityLabel,
    moveRightIconAccessibilityLabel,
    moveUpIconAccessibilityLabel,
    nextClickAction,
    previousClickAction,
    ratio,
    screenReaderAccessibilityLabel,
    skuId,
    thumbnailWidth,
    tryItAction,
    zoomLegendAccessibilityLabel,
    zoomMinusIconAccessibilityLabel,
    zoomPlusIconAccessibilityLabel,
    selectAction
  } = props;

  const carouselDesktopRef = useRef( null );
  const [selectedIndex, setSelectedIndex] = useState( 0 );
  const [selectedImage, setSelectedImage] = useState( {
    ...defaultImage,
    img: 'image',
    ariaHidden: true
  } );

  /* Sets the UUID for the rail buttons */
  const [railUuid] = useUuid();

  const [displayZoomComponent, setDisplayZoomComponent] = useState( false );
  const [isKeyBoardUser, setKeyBoardUser] = useState( false );
  const [scheduleRender] = useNextRender();
  const [updatedItems, setUpdatedItems] = useState( items );

  const isVideo = selectedImage.metaData?.mediaType === 'video';

  const displayTryIt = tryItAction?.url && tryItAction?.label && !isVideo;
  const displayZoomIcon = isKeyBoardUser && !isVideo;
  const galleryControlTabIndex = displayZoomComponent ? -1 : 0;

  const _railMeta = {
    nextAccessibility: ariaLabelForNext,
    previousAccessibility: ariaLabelForPrevious
  };

  const _railADAMeta = {
    carouselAccessibility: carouselUtils.handleTokenizedString( carouselAccessibility ),
    carouselDetailsAccessibility: carouselUtils.handleTokenizedString( carouselDetailsAccessibility )
  };

  // Listen for 3 consecutive tabs and display controls
  useKeyPressCounter(
    { keyCodes: [constants.TAB_KEY], threshold: 3 },
    { onThreshold: () => setKeyBoardUser( true ) }
  );

  // On close of zoom controller, reset focus on zoom icon
  const onZoomClose = () => {
    setDisplayZoomComponent( false );
    scheduleRender( () => {
      const zoomIcon = document.querySelector( '.CarouselDesktop__zoomIcon' );
      zoomIcon?.focus();
    } );
  };

  // Listen to skuId and reset select index + image
  useEffect( () => {
    setSelectedIndex( 0 );
    setSelectedImage( {
      ...defaultImage,
      img: 'image',
      ariaHidden: true
    } );
  }, [skuId] );

  useEffect( () => {
    if( isVideo ){
      setDisplayZoomComponent( false );
    }
  }, [isVideo] );

  // Thumbnail click handler
  const handleClick = ( e, index ) => {
    const item = items[e.currentTarget.getAttribute( 'data-index' )];
    setSelectedImage( item );
    setSelectedIndex( index );
  };

  useEffect( ()=>{
    utils.updateDataCaptureForImage( { items, selectAction }, { partialDatacaptureEventAggregator, setUpdatedItems } );
  }, [items] );

  const getCarouselItem = ( item, length, index ) => {
    if( item.metaData?.mediaType === 'img' ){
      return (
        <Image
          src={ item?.imageUrl || '' }
          alt={ item?.altText }
          key={ index }
          width={ thumbnailWidth }
          aspectRatio={ ratio }
          metaData={ item?.metaData }
          ariaHidden={ item?.ariaHidden }
        />
      );
    }
    else {
      const { w = 1, h = 1 } = item.metaData?.aspectRatio || {};
      return (
        <div className='CarouselDesktop__videoThumbnail'>
          <Image
            src={ item?.imageUrl || '' }
            alt={ item?.altText }
            key={ index }
            width={ thumbnailWidth }
            aspectRatio={ `${w}:${h}` }
            metaData={ item?.metaData }
            ariaHidden={ item?.ariaHidden }
          />
          <div className='CarouselDesktop__videoThumbnailIcon'>
            <Icon size='s'
              name='Video'
            />
          </div>
        </div>
      );
    }
  };

  const carouselItems =
    updatedItems?.length > 1 &&
    updatedItems.map( ( item, index ) => {
      let ariaCurrent = item?.imageUrl === selectedImage?.imageUrl ? 'true' : 'false';
      let carouselItem = getCarouselItem( item, items.length, index );
      return (
        <Slide key={ `${skuId}-${index}` }>
          <Button
            unstyled={ true }
            onClick={ ( e ) => handleClick( e, index ) }
            className={ classNames( 'imgButton', {
              'imgButton--Video': item.metaData?.mediaType === 'video',
              'imgButton--selected': selectedIndex === index,
              'imgButton--unselected': selectedIndex !== index
            } ) }
            ariaLabel={ carouselUtils.handleTokenizedString( carouselCardAccessibility, [
              index + 1,
              items.length
            ] ) }
            dataIndex={ index }
            action={ item.action }
            ariaCurrent={ ariaCurrent }
          >
            { carouselItem }
          </Button>
        </Slide>
      );
    } );

  return (
    <div className='CarouselDesktop'
      role='region'
      aria-label={ carouselAccessibility }
      ref={ carouselDesktopRef }
    >
      <div className='CarouselDesktop__mainImgBlock'>
        <ZoomController
          mediaContent={ selectedImage }
          onClose={ () => onZoomClose() }
          show={ displayZoomComponent }
          zoomPlusIconAccessibilityLabel={ zoomPlusIconAccessibilityLabel }
          zoomMinusIconAccessibilityLabel={ zoomMinusIconAccessibilityLabel }
          moveUpIconAccessibilityLabel={ moveUpIconAccessibilityLabel }
          moveDownIconAccessibilityLabel={ moveDownIconAccessibilityLabel }
          moveLeftIconAccessibilityLabel={ moveLeftIconAccessibilityLabel }
          moveRightIconAccessibilityLabel={ moveRightIconAccessibilityLabel }
          closeAccessibilityLabel={ closeAccessibilityLabel }
          screenReaderAccessibilityLabel={ screenReaderAccessibilityLabel }
          moveLegendAccessibilityLabel={ moveLegendAccessibilityLabel }
        />
        <MediaWrapper
          badges={ badges }
          mediaContent={ selectedImage }
          componentKey={ componentKey }
          show={ !displayZoomComponent }
        />
        <div
          className={ classNames( 'CarouselDesktop__mainImgBlockControls', {
            'CarouselDesktop__mainImgBlockControls--show': !displayZoomComponent
          } ) }
        >
          { displayTryIt && (
            <Link_Huge
              compact={ true }
              action={ tryItAction }
              ariaLabel={ `Try it ${defaultImage.altText}` }
              tabIndex={ galleryControlTabIndex }
            >
              <Icon size='s'
                name={ 'Tryon' }
              />
              { tryItAction.label }
            </Link_Huge>
          ) }
          { displayZoomIcon && (
            <Button
              icon
              iconImage='ZoomIn'
              iconSize='m'
              className='CarouselDesktop__zoomIcon'
              onClick={ () => {
                setDisplayZoomComponent( true );
              } }
              tabIndex={ galleryControlTabIndex }
              ariaLabel={ zoomLegendAccessibilityLabel }
              ariaHidden={ displayZoomComponent }
            />
          ) }
        </div>
      </div>
      { items?.length > 1 && (
        <div className='CarouselDesktop__thumbnails'>
          <Rail
            railRef={ carouselDesktopRef }
            id={ railUuid }
            items={ items }
            previousClickAction={ previousClickAction }
            nextClickAction={ nextClickAction }
            _railADAmeta={ _railADAMeta }
            _railMeta={ _railMeta }
            useObserver={ true }
            useObserveSlideChildren={ true }
            sliderPerViewXL={ 6 }
            slidesPerGroupXL={ 1 }
            sliderPerViewLG={ 6 }
            slidesPerGroupLG={ 1 }
          >
            { carouselItems }
          </Rail>
        </div>
      ) }
    </div>
  );
};

/**
 * Provides data capture data for image clicks and sets it using the provided method.
 *
 * @param {Object} methods - An object containing methods for handling the fetched data.
 * @param {object} data - arguments
 */

export const updateDataCaptureForImage = ( data, method )=> {
  const { items, selectAction } = handleEmptyObjects( data );
  const { partialDatacaptureEventAggregator, setUpdatedItems } = method;

  if( !hasItems( items ) || !selectAction || !partialDatacaptureEventAggregator ){
    return;
  }

  const updatedDataCaptureData = items.map( ( item )=>{
    const updatedDataCaptureEvent = partialDatacaptureEventAggregator( {
      action: { ...selectAction, dataCaptureData: selectAction?.dataCaptureData },
      selectedItems: [{ partialDataCaptureEvent: item.partialDataCaptureEvent }]
    } );
    return {
      ...item,
      action: {
        ...item.action,
        dataCaptureData: updatedDataCaptureEvent?.dataCaptureData
      }
    };
  } );
  setUpdatedItems( updatedDataCaptureData );
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {string} ariaLabelForNext='' - The default value of the ariaLabelForNext is Go to next slide.
 * @property {string} ariaLabelForPrevious='' - The default value of the ariaLabelForPrevious is Go to previous slide.
 * @property {object} defaultImage='' - The default image src for main image
 */
export const defaultProps = {
  ariaLabelForNext: '',
  ariaLabelForPrevious: '',
  defaultImage: {}
};

/**
 * Property type definitions
 * @typedef CarouselDesktopProps
 * @type {object}
 * @property {array} items - The array of assets
 * @property {number} thumbnailWidth - Sets the width of image thumbnails
 * @property {string} ratio - Sets the ratio of image
 * @property {string} ariaLabelForNext - Set the value for ariaLabel of next button
 * @property {string} ariaLabelForPrevious - Set the value for ariaLabel of previous button
 * @property {object} defaultImage - Sets the default image of main.
 * @property {object} tryItAction - Sets the label and url for try it link
 * @property {string} carouselAccessibility -  Sets aria label value for carousel wrapper
 * @property {string} carouselDetailsAccessibility - Sets accessibility label value for carousel details
 * @property {string} carouselCardAccessibility - Sets the carousel card accessibility
 * @property {string} skuId - Sets the carousel images sku ID
 * @property {object} previousClickAction - Sets the action properties for the carousel 'previous' button
 * @property {object} nextClickAction - Sets the action properties for the carousel 'next' button
 * @property {string} zoomLegendAccessibilityLabel - Set the value for ariaLabel of zoom icon
 * @property {string} zoomPlusIconAccessibilityLabel - Set the value for ariaLabel of plus icon
 * @property {string} zoomMinusIconAccessibilityLabel - Set the value for ariaLabel of minus icon
 * @property {string} moveUpIconAccessibilityLabel - Set the value for ariaLabel of zoom up icon
 * @property {string} moveDownIconAccessibilityLabel - Set the value for ariaLabel of zoom down icon
 * @property {string} moveLeftIconAccessibilityLabel - Set the value for ariaLabel of zoom left icon
 * @property {string} moveRightIconAccessibilityLabel - Set the value for ariaLabel of zoom right icon
 * @property {string} closeAccessibilityLabel -Set the value for ariaLabel of zoom close icon
 * @property {string} screenReaderAccessibilityLabel -Set the value for ariaLabel of image.
 * @property {string} moveLegendAccessibilityLabel -Set the value for sr-only class of image.
 * @property {array} badges - shows badges
 */
export const propTypes = {
  /**
   * The elements to display as slides in the carousel
   */
  items: PropTypes.array.isRequired,
  /**
   * The image width in carousel
   */
  thumbnailWidth: PropTypes.number,
  /**
   * The ratio of image
   */
  ratio: PropTypes.string,
  /**
   * The aria-label for next button
   */
  ariaLabelForNext: PropTypes.string,
  /**
   * The aria-label for previous button
   */
  ariaLabelForPrevious: PropTypes.string,
  /**
   *  The prop that sets defaultImage of main image
   */
  defaultImage: PropTypes.object,
  /**
   *  The prop that sets the try it label and link url
   */
  tryItAction: PropTypes.object,
  /**
   *  The prop that sets aria label value for carousel wrapper
   */
  carouselAccessibility: PropTypes.string,
  /**
   *  The prop that sets accessibilty value for carousel details
   */
  carouselDetailsAccessibility: PropTypes.string,
  /**
   * The prop that holds carousel card accessibility
   */
  carouselCardAccessibility: PropTypes.string,
  /**
   * The prop that holds carousel images sku ID
   */
  skuId: PropTypes.string,
  /**
   *  The prop that sets the carousel 'previous' button action properties
   */
  previousClickAction: PropTypes.object,
  /**
   *  The prop that sets the carousel 'next' button action properties
   */
  nextClickAction: PropTypes.object,
  /**
   *  The aria-label for zoom button
   */
  zoomLegendAccessibilityLabel: PropTypes.string,
  /**
   *  The aria-label for zoom plus button
   */
  zoomPlusIconAccessibilityLabel: PropTypes.string,
  /**
   *  The aria-label for zoom minus button
   */
  zoomMinusIconAccessibilityLabel: PropTypes.string,
  /**
   *  The aria-label for zoom up button
   */
  moveUpIconAccessibilityLabel: PropTypes.string,
  /**
   *  The aria-label for zoom down button
   */
  moveDownIconAccessibilityLabel: PropTypes.string,
  /**
   *  The aria-label for zoom left button
   */
  moveLeftIconAccessibilityLabel: PropTypes.string,
  /**
   *  The aria-label for zoom right button
   */
  moveRightIconAccessibilityLabel: PropTypes.string,
  /**
    *  The aria-label for close button
    */
  closeAccessibilityLabel: PropTypes.string,
  /**
    *  The aria-label of image
    */
  screenReaderAccessibilityLabel: PropTypes.string,
  /**
    *  The sr-only class of image.
    */
  moveLegendAccessibilityLabel: PropTypes.string,
  /**
    *  prop that shows badges.
    */
  badges: PropTypes.array
};

CarouselDesktop.propTypes = propTypes;
CarouselDesktop.defaultProps = defaultProps;

export default CarouselDesktop;
